import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { format } from 'date-fns';

const roleLabel  = { super_admin: 'Super Admin', admin_pesquisa: 'Admin Pesquisa', pesquisador: 'Pesquisador', visualizador: 'Visualizador' };
const roleOptions = [
  { value: 'super_admin',    label: 'Super Admin' },
  { value: 'admin_pesquisa', label: 'Admin Pesquisa' },
  { value: 'pesquisador',    label: 'Pesquisador' },
  { value: 'visualizador',   label: 'Visualizador' },
];

function NovoUsuarioModal({ onClose, onSaved }) {
  const [form, setForm]     = useState({ nome_completo: '', email: '', senha: '', confirmar_senha: '', role: 'pesquisador' });
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave() {
    if (!form.nome_completo || !form.email || !form.senha || !form.role) {
      setError('Preencha todos os campos obrigatórios.'); return;
    }
    if (form.senha.length < 8) {
      setError('A senha deve ter no mínimo 8 caracteres.'); return;
    }
    if (form.senha !== form.confirmar_senha) {
      setError('As senhas não coincidem.'); return;
    }
    setLoading(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/criar-usuario`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            nome_completo: form.nome_completo,
            email: form.email,
            senha: form.senha,
            role: form.role,
          }),
        }
      );
      const result = await response.json();
      if (result.success) {
        onSaved();
      } else {
        setError(result.error || 'Erro ao criar usuário.');
      }
    } catch (e) {
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>Novo usuário</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>⚠ {error}</div>}
          <div className="form-group">
            <label className="form-label">Nome completo *</label>
            <input className="form-input" value={form.nome_completo} onChange={e => set('nome_completo', e.target.value)} autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">E-mail *</label>
            <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Senha * (mínimo 8 caracteres)</label>
            <input className="form-input" type="password" value={form.senha} onChange={e => set('senha', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Confirmar senha *</label>
            <input className="form-input" type="password" value={form.confirmar_senha} onChange={e => set('confirmar_senha', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Papel *</label>
            <select className="form-select" value={form.role} onChange={e => set('role', e.target.value)}>
              {roleOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Criando...' : 'Criar usuário'}
          </button>
        </div>
      </div>
    </div>
  );
}
const roleBadge = { super_admin: 'badge-blue', admin_pesquisa: 'badge-teal', pesquisador: 'badge-green', visualizador: 'badge-gray' };

function formatCpf(cpf) {
  if (!cpf) return '—';
  const d = cpf.replace(/\D/g, '');
  if (d.length !== 11) return cpf;
  return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`;
}

export default function UsuariosPage() {
  const { profile: myProfile, approveUser, isSuperAdmin } = useAuth();
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [approving, setApproving] = useState(null);
  const [feedback, setFeedback]   = useState('');
  const [showNovo, setShowNovo]   = useState(false);

  async function load() {
    setLoading(true);
    const { data } = await supabase.from('profiles').select('*').order('criado_em');
    setUsers(data || []);
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  async function changeRole(id, role) {
    await supabase.from('profiles').update({ role }).eq('id', id);
    load();
  }

  async function toggleAtivo(id, ativo) {
    await supabase.from('profiles').update({ ativo: !ativo }).eq('id', id);
    load();
  }

  async function handleApprove(u) {
    setApproving(u.id);
    try {
      await approveUser(u.id);
      setFeedback(`Usuário ${u.nome_completo || u.email} aprovado com sucesso.`);
      setTimeout(() => setFeedback(''), 4000);
      load();
    } catch {
      setFeedback('Erro ao aprovar. Tente novamente.');
      setTimeout(() => setFeedback(''), 4000);
    } finally {
      setApproving(null);
    }
  }

  const pendentes = users.filter(u => !u.aprovado);
  const aprovados = users.filter(u => u.aprovado);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Usuários</h1>
          <p>Gerencie papéis, acessos e aprovações da equipe de pesquisa.</p>
        </div>
        {isSuperAdmin && (
          <button className="btn btn-primary" onClick={() => setShowNovo(true)}>+ Novo usuário</button>
        )}
      </div>

      {feedback && (
        <div className="alert alert-success" style={{ marginBottom: 16 }}>✓ {feedback}</div>
      )}

      {/* ── Pendentes de aprovação ── */}
      {pendentes.length > 0 && (
        <div className="card" style={{ marginBottom: 24, borderColor: '#fbbf24' }}>
          <div className="card-header" style={{ background: '#fffbeb' }}>
            <h2 style={{ color: '#92400e' }}>⏳ Aguardando Aprovação ({pendentes.length})</h2>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>E-mail</th>
                  <th>CPF</th>
                  <th>Cadastrado em</th>
                  <th>Ação</th>
                </tr>
              </thead>
              <tbody>
                {pendentes.map(u => (
                  <tr key={u.id}>
                    <td><strong>{u.nome_completo || '—'}</strong></td>
                    <td className="text-muted text-sm">{u.email}</td>
                    <td><span className="mono text-sm">{formatCpf(u.cpf)}</span></td>
                    <td className="text-muted text-sm">{format(new Date(u.criado_em), 'dd/MM/yyyy')}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-primary"
                        disabled={approving === u.id}
                        onClick={() => handleApprove(u)}
                      >
                        {approving === u.id ? 'Aprovando...' : '✓ Aprovar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Usuários aprovados ── */}
      <div className="card">
        <div className="card-header">
          <h2>Equipe de pesquisa ({aprovados.length})</h2>
        </div>
        <div className="table-wrap">
          {loading ? (
            <div style={{ padding: 32, textAlign: 'center' }} className="text-muted text-sm">Carregando...</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>E-mail</th>
                  <th>CPF</th>
                  <th>Papel</th>
                  <th>Status</th>
                  <th>Desde</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {aprovados.map(u => (
                  <tr key={u.id}>
                    <td><strong>{u.nome_completo}</strong></td>
                    <td className="text-muted text-sm">{u.email}</td>
                    <td><span className="mono text-sm">{formatCpf(u.cpf)}</span></td>
                    <td>
                      {u.id === myProfile?.id ? (
                        <span className={`badge ${roleBadge[u.role]}`}>{roleLabel[u.role]}</span>
                      ) : (
                        <select
                          className="form-select"
                          style={{ padding: '4px 8px', fontSize: 13, width: 'auto' }}
                          value={u.role}
                          onChange={e => changeRole(u.id, e.target.value)}
                        >
                          <option value="visualizador">Visualizador</option>
                          <option value="pesquisador">Pesquisador</option>
                          <option value="admin_pesquisa">Admin Pesquisa</option>
                          <option value="super_admin">Super Admin</option>
                        </select>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${u.ativo ? 'badge-green' : 'badge-gray'}`}>
                        {u.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="text-muted text-sm">{format(new Date(u.criado_em), 'dd/MM/yyyy')}</td>
                    <td>
                      {u.id !== myProfile?.id && (
                        <button
                          className={`btn btn-sm ${u.ativo ? 'btn-secondary' : 'btn-primary'}`}
                          onClick={() => toggleAtivo(u.id, u.ativo)}
                        >
                          {u.ativo ? 'Desativar' : 'Reativar'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showNovo && (
        <NovoUsuarioModal
          onClose={() => setShowNovo(false)}
          onSaved={() => {
            setShowNovo(false);
            setFeedback('Usuário criado com sucesso.');
            setTimeout(() => setFeedback(''), 4000);
            load();
          }}
        />
      )}
    </div>
  );
}
